import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash/fp';
import { ChangeEvent, useState } from 'react';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import FormEditCaseEmailTemplate, {
  FormEditCaseEmailTemplateData,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/FormEditCaseEmailTemplate';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { CaseEmailTemplate } from 'dashboard/models/case-template';
import { ModuleCaseEmail } from 'dashboard/modules/CaseEmail';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import useToggle from 'lib/hooks/useToggle';

const SettingsContainer = styled('div')`
  padding: ${Spacing[5]};
`;

const Row = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: 1fr 1fr;
  align-items: center;
`;

const EmailTemplateHeader = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: 2fr 1fr;
  align-items: center;
`;

const EmailTemplatesContainer = styled('div')`
  border-top: 1px solid ${({ theme }) => theme.color.background.secondary};
  padding: ${Spacing[5]};
`;

const StyledTableTD = styled(TableTD)`
  width: 100%;
`;

export interface CaseEmailPanelProps {
  module: ModuleCaseEmail;
  caseEmailTemplates: CaseEmailTemplate[];
  updateModule: (module: ModuleCaseEmail) => void;
  addEmailTemplate: () => CaseEmailTemplate;
  deleteEmailTemplate: (token: string) => void;
  updateEmailTemplate: (updatedEmailTemplate: CaseEmailTemplate) => void;
}

const CaseEmailPanel = ({
  module,
  caseEmailTemplates,
  updateModule,
  addEmailTemplate,
  deleteEmailTemplate,
  updateEmailTemplate,
}: CaseEmailPanelProps) => {
  const personaDomain = window.location.hostname.includes('withpersona-staging')
    ? '@cases.withpersona-staging.com'
    : '@cases.withpersona.com';
  const hasError =
    module['default-from-email-address'] == null
      ? true
      : !EMAIL_ADDRESS_REGEX.test(module['default-from-email-address']);
  const [
    editEmailTemplateModal,
    { enable: showEditEmailTemplateModal, disable: hideEditEmailTemplateModal },
  ] = useToggle(false);
  const [currentEmailTemplate, setCurrentEmailTemplate] = useState<CaseEmailTemplate>();

  const updateAttributes = (e: ChangeEvent<HTMLInputElement>) => {
    const value = isEmpty(e.target.value) ? e.target.value : e.target.value + personaDomain;
    updateModule({
      ...module,
      'default-from-email-address': value,
    });
  };

  const moreItems = (emailTemplate: CaseEmailTemplate) => {
    return [
      {
        id: 'case-email-panel-templates__edit_button',
        label: 'Edit',
        variant: 'basic' as const,
        onClick: () => {
          setCurrentEmailTemplate(emailTemplate);
          showEditEmailTemplateModal();
        },
      },
      {
        id: 'case-email-panel-templates__delete_button',
        label: 'Delete',
        variant: 'danger' as const,
        onClick: () => deleteEmailTemplate(emailTemplate.token),
      },
    ];
  };

  const handleUpdateEmailTemplate = (emailTemplateData: FormEditCaseEmailTemplateData) => {
    updateEmailTemplate(emailTemplateData);
    hideEditEmailTemplateModal();
  };

  const handleAddEmailTemplate = () => {
    const addedEmailTemplate = addEmailTemplate();
    setCurrentEmailTemplate(addedEmailTemplate);
    showEditEmailTemplateModal();
  };

  return (
    <>
      <SettingsContainer>
        <Heading size='sm'>Settings</Heading>
        <Spacer height={3} />
        <Label
          size='md'
          weight='bold'
          id='case-email-panel__default-from-email-address-prefix--label'
        >
          Default sender address
        </Label>
        <Row>
          <Input
            id='case-email-panel__default-from-email-address-prefix'
            aria-labelledby='case-email-panel__default-from-email-address-prefix--label'
            type='email'
            placeholder='email prefix'
            hasError={hasError}
            value={
              module['default-from-email-address'] == null
                ? ''
                : module['default-from-email-address'].substring(
                    0,
                    module['default-from-email-address'].lastIndexOf('@'),
                  )
            }
            onChange={updateAttributes}
          />
          <Paragraph color='primary'>{personaDomain}</Paragraph>
        </Row>
        {hasError && <FormError>Please enter a valid email prefix.</FormError>}
      </SettingsContainer>
      <Spacer height={1} />
      <EmailTemplatesContainer>
        <EmailTemplateHeader>
          <Heading size='sm'>Email Templates</Heading>
          <Button
            id='case-email-panel__add-email-template'
            variant='inverse'
            prefixIcon={<PlusIcon />}
            width='auto'
            onClick={handleAddEmailTemplate}
          >
            Add
          </Button>
        </EmailTemplateHeader>
        <Spacer height={1} />
        <TableLayout
          showHeader={caseEmailTemplates.length > 0}
          isEmpty={caseEmailTemplates.length === 0}
          nullState={<></>}
          headerTR={
            <>
              <TableTH>Template</TableTH>
              <TableTH></TableTH>
            </>
          }
        >
          {caseEmailTemplates.map((emailTemplate) => {
            return (
              <TableTR key={emailTemplate.token}>
                <StyledTableTD>{emailTemplate.name}</StyledTableTD>
                <StyledTableTD>
                  <DropdownMenu
                    trigger={
                      <Button
                        id='case-email-panel-row__dropdown'
                        variant='inverse'
                        width='icon'
                        aria-label='More'
                      >
                        <MoreIcon />
                      </Button>
                    }
                  >
                    {moreItems(emailTemplate).map((item) => (
                      <DropdownMenu.Item
                        id={item.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          item.onClick();
                        }}
                        key={item.id}
                        variant={item.variant}
                      >
                        {item.label}
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu>
                </StyledTableTD>
              </TableTR>
            );
          })}
        </TableLayout>
      </EmailTemplatesContainer>
      <ModalWrapper isOpen={editEmailTemplateModal} handleCloseModal={hideEditEmailTemplateModal}>
        <ModalContainer>
          <ModalHeader onClose={hideEditEmailTemplateModal}>
            Edit {currentEmailTemplate?.name}
          </ModalHeader>
          <ModalBody>
            <FormEditCaseEmailTemplate
              defaultValues={{
                name: currentEmailTemplate?.name ?? '',
                token: currentEmailTemplate?.token ?? '',
                'creator-id': currentEmailTemplate?.['creator-id'] ?? '',
                cc: currentEmailTemplate?.cc ?? [],
                subject: currentEmailTemplate?.subject ?? '',
                body: currentEmailTemplate?.body ?? '',
              }}
              handleClose={hideEditEmailTemplateModal}
              handleUpdateEmailTemplate={handleUpdateEmailTemplate}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

export default CaseEmailPanel;
